import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { whatsappLink, WhatsAppIcon } from "@/lib/whatsapp";
import { Sparkles, Gem, HeartHandshake } from "lucide-react";

const BRIDAL_HERO = "https://customer-assets.emergentagent.com/job_riddhi-elegance/artifacts/vlw7o2zy_7769cdbb-7aa1-4458-aee9-a7bdd155cacf.jpg";

export default function Bridal() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { tag: "bridal", limit: 50 } })
      .then(({ data }) => setProducts(data));
  }, []);

  const enquireText = "Hi Riddhi, I'd like to book a bridal appointment. Could you share availability at the Mahalaxmi Layout atelier?";
  const bookingHref = `https://wa.me/918660515091?text=${encodeURIComponent(enquireText)}`;

  return (
    <div data-testid="bridal-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#1C1917]">
        <div className="grid grid-cols-1 lg:grid-cols-12 min-h-[88vh]">
          <div className="lg:col-span-5 flex items-center px-6 md:px-12 lg:px-16 py-20 relative z-10">
            <div className="fade-up">
              <div className="text-[11px] tracking-overline uppercase text-[#E9C97A] mb-6">The Bridal Edit</div>
              <h1 className="font-serif-h font-light text-5xl sm:text-6xl md:text-7xl text-[#FDFBF7] leading-[0.95]">
                Bridal<br /><em className="italic text-[#E9C97A]">Collections</em>
              </h1>
              <p className="mt-8 text-stone-200 font-light text-base md:text-lg max-w-lg leading-relaxed">
                Heirloom Kanjeevarams, temple-border silks, and regal lehengas — handpicked by Meena & Naveen Raghu
                for the women who will wear them into forever.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <a href="#bridal-edit"
                   data-testid="bridal-collections-btn"
                   className="inline-flex items-center gap-3 bg-[#E9C97A] hover:bg-[#D4AF37] text-[#1C1917] px-8 py-4 rounded-sm font-medium tracking-wide text-sm uppercase transition-colors">
                  Bridal Collections
                </a>
                <a href={bookingHref} target="_blank" rel="noreferrer"
                   className="border border-[#E9C97A] text-[#E9C97A] hover:bg-[#E9C97A] hover:text-[#1C1917] px-8 py-4 rounded-sm font-medium tracking-wide text-sm uppercase transition-all inline-flex items-center gap-3">
                  <WhatsAppIcon size={14} /> Enquire on WhatsApp
                </a>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7 relative min-h-[480px] lg:min-h-[88vh] bg-[#0e0b0a]">
            <img src={BRIDAL_HERO} alt="Riddhi Bridal Collection"
                 className="absolute inset-0 w-full h-full object-contain object-center lg:object-right" />
            <div className="absolute inset-0 lg:bg-gradient-to-r from-[#1C1917] via-transparent to-transparent" />
            <div className="absolute bottom-6 right-6 hidden lg:block text-right">
              <div className="text-[10px] tracking-overline uppercase text-[#E9C97A]/70">Muhurtha Silk · Kanchipuram</div>
            </div>
          </div>
        </div>
      </section>

      {/* VALUE STRIP */}
      <section className="px-6 md:px-12 lg:px-24 py-20 border-b border-warm">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {[
            { Icon: Sparkles, t: "Curated Heirlooms", d: "Only pure-silk weaves from master looms in Kanchipuram, Mysore & Benares." },
            { Icon: Gem, t: "Private Consultations", d: "One-on-one fittings at our Mahalaxmi Layout atelier, with traditional jewellery styling." },
            { Icon: HeartHandshake, t: "Every Bride, Bespoke", d: "Blouse tailoring, pallu finishing, and custom draping — all crafted in-house." },
          ].map(({ Icon, t, d }) => (
            <div key={t} className="flex gap-5">
              <Icon strokeWidth={1} className="text-[#D4AF37] flex-shrink-0 mt-1" size={30} />
              <div>
                <div className="text-[11px] tracking-overline uppercase text-stone-500 mb-2">{t}</div>
                <p className="text-stone-700 font-light leading-relaxed">{d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* EDIT */}
      <section id="bridal-edit" className="px-6 md:px-12 lg:px-24 py-24">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <div className="text-[11px] tracking-overline uppercase text-[#5A1618] mb-3">The Edit</div>
            <h2 className="font-serif-h font-light text-4xl md:text-5xl text-stone-900">Bridal Silks & Lehengas</h2>
          </div>
          <a href={bookingHref} target="_blank" rel="noreferrer"
             className="text-[11px] tracking-overline uppercase text-[#D4AF37] hover:underline">
            Can't find your drape? DM us →
          </a>
        </div>

        {products.length === 0 ? (
          <div className="text-center py-16 border border-warm">
            <div className="font-serif-h text-2xl text-stone-800">New bridal edit coming soon</div>
            <p className="text-stone-500 mt-2 text-sm">Meanwhile, visit us at the atelier or DM for a private preview.</p>
            <a href={bookingHref} target="_blank" rel="noreferrer" className="btn-ink mt-6 inline-flex">
              <WhatsAppIcon size={14} /> DM on WhatsApp
            </a>
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {products.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
      </section>

      {/* CLOSING BANNER */}
      <section className="relative my-10 overflow-hidden">
        <div className="relative h-[420px] bg-[#5A1618] grain">
          <div className="absolute inset-0 flex items-center justify-center text-center px-6">
            <div className="max-w-2xl">
              <div className="text-[11px] tracking-overline uppercase text-[#E9C97A] mb-4">Bridal Atelier</div>
              <h3 className="font-serif-h font-light text-4xl md:text-5xl text-[#FDFBF7] leading-tight">
                Every drape deserves to be chosen in person.
              </h3>
              <p className="mt-5 text-stone-200 font-light">
                Book a private one-hour appointment at our Mahalaxmi Layout atelier in Bengaluru.
              </p>
              <a href={bookingHref} target="_blank" rel="noreferrer"
                 className="mt-8 inline-flex items-center gap-3 bg-[#E9C97A] hover:bg-[#D4AF37] text-[#1C1917] px-8 py-4 rounded-sm font-medium tracking-wide text-sm uppercase transition-colors">
                <WhatsAppIcon size={16} /> Book via WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
